import { useEffect, useRef, useState } from 'react'
import { flushSync } from 'react-dom'

import { EMPTY_OBJECT } from '../../../../constants/common'
import { useMinimizedModalsContext } from '../../../../hooks'

import { MINIMIZABLE_MODAL_REPLACE_CONFIRMATION } from './useMinimizableModal.constants'
import {
  getCloseConfirmation,
  getReplaceConfirmation,
} from './useMinimizableModal.utils'

export function useMinimizableModal({
  closeConfirmation,
  minimizeKey,
  qaName,
  replaceConfirmation = MINIMIZABLE_MODAL_REPLACE_CONFIRMATION,
  scope,
  sourceId,
  title,
  onClose,
} = EMPTY_OBJECT) {
  const context = useMinimizedModalsContext()

  const [isMinimized, setMinimized] = useState(false)

  const onCloseRef = useRef(onClose)
  const isDirtyRef = useRef(false)
  const ownsEntryRef = useRef(false)

  useEffect(() => {
    onCloseRef.current = onClose
  }, [onClose])

  function unregister() {
    if (ownsEntryRef.current) {
      context?.unregister(minimizeKey)
      ownsEntryRef.current = false
    }
  }

  useEffect(() => {
    if (context && minimizeKey) {
      return () => {
        unregister()
      }
    }
  }, [])

  function setIsDirty(value) {
    isDirtyRef.current = !!value
  }

  function getIsDirty() {
    return isDirtyRef.current
  }

  function closeModal() {
    setMinimized(false)
    unregister()
    onCloseRef.current?.()
  }

  function handleRestore() {
    setMinimized(false)
    unregister()
  }

  function handleConfirmationCancel() {
    context?.hideConfirmation()
    setMinimized(true)
  }

  function handleClose() {
    if (!isDirtyRef.current) {
      closeModal()

      return
    }

    context?.showConfirmation(
      getCloseConfirmation({ closeConfirmation, onSubmit: closeModal })
    )
  }

  function registerAndMinimize() {
    context.register({
      getIsDirty,
      key: minimizeKey,
      qaName,
      replaceConfirmation,
      scope,
      sourceId,
      title,
      onClose: handleClose,
      onConfirmationCancel: handleConfirmationCancel,
      onForceClose: closeModal,
      onRestore: handleRestore,
    })

    ownsEntryRef.current = true
    setMinimized(true)
  }

  function getEntryToDisplace() {
    if (context.hasEntry(minimizeKey)) {
      return ownsEntryRef.current ? null : context.getEntry(minimizeKey)
    }

    return context.getReplacementCandidate(minimizeKey) ?? null
  }

  function handleMinimize() {
    if (context && minimizeKey) {
      const entryToDisplace = getEntryToDisplace()

      if (!entryToDisplace) {
        registerAndMinimize()

        return
      }

      context.showConfirmation(
        getReplaceConfirmation({
          displacedEntry: entryToDisplace,
          onSubmit: () => {
            flushSync(() => {
              entryToDisplace.onForceClose?.()
            })

            registerAndMinimize()
          },
        })
      )
    }
  }

  return {
    handleMinimize,
    handleRestore,
    isMinimized,
    setIsDirty,
  }
}
